import type { Vehicle } from './vehicles'
import { pkr } from '../utils/format'

/* One record of who we are and how to reach us. Navbar, Footer, contact page
   and every WhatsApp CTA read from here, so a changed number changes once. */

export const SITE = {
  name: 'Sultan Motors',
  suffix: '28',
  tagline: 'The Car Lounge',
  city: 'Islamabad',
  /* The one number the business answers on — calls and WhatsApp both. There
     was a second, general line here once, and it was a placeholder nobody
     picked up; a car enquiry and a phone call now land on the same desk. */
  phone: '+92 339 1242426',
  phoneHref: 'tel:+923391242426',
  whatsapp: '923391242426',
  /* Kept as its own key because the vehicle CTAs read it by name, but it is
     the same desk as `whatsapp` above — write both if the number ever moves. */
  salesWhatsapp: '923391242426',
  email: 'hello@sultanmotors28.com',
  address: 'Blue Area, Islamabad, Pakistan',
  hours: 'Mon – Sun · 10:00 to 20:00',
} as const

/* Which WhatsApp a link opens.

   wa.me is a redirector, not a destination: on a desktop it lands on an
   interstitial asking whether to use the desktop app or continue to the web
   one — a click, and a decision, in between "I like this car" and sending
   the message. web.whatsapp.com/send skips it and opens the conversation
   with the text already written.

   The same URL is wrong on a phone: WhatsApp Web does not run there, so it
   answers with a page saying so instead of opening the app that is already
   installed a tap away. The host is therefore chosen from the device rather
   than fixed, and the phone keeps the deep link it had. */
const MOBILE = /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|BlackBerry/i

const onPhone = () =>
  typeof navigator !== 'undefined' && MOBILE.test(navigator.userAgent)

/** A WhatsApp link, optionally opening with the message pre-written. */
export function whatsappHref(message?: string, number: string = SITE.whatsapp) {
  const text = message ? encodeURIComponent(message) : ''

  if (onPhone()) {
    return text ? `https://wa.me/${number}?text=${text}` : `https://wa.me/${number}`
  }

  const web = `https://web.whatsapp.com/send?phone=${number}`
  return text ? `${web}&text=${text}` : web
}

/** The public URL of a car's page — what gets pasted into the message. */
export function vehicleUrl(id: string) {
  /* Absolute, because the recipient opens it from a chat and a relative path
     means nothing there. Falls back to the production host during a build,
     where there is no window to ask. */
  const origin =
    typeof window === 'undefined' ? 'https://sultanmotors28.com' : window.location.origin
  return `${origin}/cars/${id}`
}

/* The enquiry, written out. Everything the desk needs to answer without a
   second message: which car, what it costs, what the price covers, and the
   link back to the listing the customer was looking at.

   `colour` is the paint the customer had selected when they pressed the
   button. It is the one thing on this message the desk cannot work out from
   the link, and it is the first question they would otherwise have to ask. */
export function vehicleEnquiry(vehicle: Vehicle, colour?: string) {
  const covered = [
    vehicle.inclusions.insurance && 'insurance',
    vehicle.inclusions.registration && 'registration',
    vehicle.inclusions.withholdingTax && 'withholding tax',
    vehicle.inclusions.tracker && 'tracker',
  ].filter(Boolean)

  return [
    `Hello ${SITE.name} ${SITE.suffix} — I am interested in this car.`,
    '',
    `*${vehicle.title}*`,
    vehicle.description,
    '',
    `Price: PKR ${pkr.format(vehicle.price)}`,
    /* The car's own terms, not the standard ones written out as a constant:
       a promotional deposit has to reach the message a customer keeps. */
    vehicle.instalment
      ? `Instalment: from PKR ${pkr.format(vehicle.instalment)}/month${
          vehicle.deposit === null
            ? ''
            : ` (PKR ${pkr.format(vehicle.deposit)} down, 5 years)`
        }`
      : 'Financing: cash purchase only',
    ...(colour ? [`Colour: ${colour}`] : []),
    covered.length ? `Included: ${covered.join(', ')}` : 'Included: nothing — ex-showroom price',
    '',
    vehicleUrl(vehicle.id),
    '',
    'Please share availability and the next step.',
  ].join('\n')
}

/** The WhatsApp link a vehicle card's button opens. */
export const vehicleWhatsappHref = (vehicle: Vehicle, colour?: string) =>
  whatsappHref(vehicleEnquiry(vehicle, colour), SITE.salesWhatsapp)

/** Primary navigation — six items, in order.

    Cars and Pages open a panel rather than going straight somewhere: Cars is
    the mega menu (brands from live stock, budgets, the featured car) and
    Pages the short list of everything else on the site. Each still carries a
    `to`, which is where a tap on the item itself — on a phone, or with a
    keyboard — lands. */
export type NavMenu = 'cars' | 'pages'

export type NavLink = {
  to: string
  label: string
  menu?: NavMenu
}

export const NAV_LINKS: readonly NavLink[] = [
  { to: '/home', label: 'Home' },
  { to: '/policy', label: 'Policy' },
  { to: '/home#cars', label: 'Cars', menu: 'cars' },
  { to: '/financing', label: 'Financing' },
  { to: '/about', label: 'Pages', menu: 'pages' },
  { to: '/contact', label: 'Contact' },
]

/** What the Pages panel lists: every route that is not already in the bar. */
export const PAGE_LINKS = [
  { to: '/about', label: 'About us', blurb: 'A message from Sultan Rao, CEO of Sultan Motors.' },
  { to: '/financing', label: 'How financing works', blurb: 'Six steps, one file, every bank.' },
  { to: '/financing#faq', label: 'Questions answered', blurb: 'Markup, deposits, documents, timing.' },
  { to: '/home#video-reviews', label: 'Delivery day', blurb: 'Thirty-second reviews from the handover.' },
  { to: '/contact', label: 'Visit the lounge', blurb: 'Open seven days, 10:00 to 20:00.' },
] as const

/** The budget bands the Cars panel offers, in rupees. */
export const BUDGET_LINKS = [
  { label: 'Under 40 lac', max: 4_000_000 },
  { label: '40 – 60 lac', min: 4_000_000, max: 6_000_000 },
  { label: '60 – 90 lac', min: 6_000_000, max: 9_000_000 },
  { label: '90 lac – 1.5 cr', min: 9_000_000, max: 15_000_000 },
  { label: 'Above 1.5 cr', min: 15_000_000 },
] as const
